import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import FvAssessment from "@/components/FvAssessment";
import ProtocolInfo from "@/components/ProtocolInfo";

/**
 * Increment A — adaptive "Record Test" form. Test selection comes first; the
 * fields adapt to the chosen test. Reuses the existing approved definitions
 * (matched by name) and posts to the single canonical /physical-tests/results
 * endpoint with structured `metrics` so the backend can derive and store
 * take-off velocity / RSI / relative strength / peak force (N).
 *
 * Original units (cm, kg, ms, kgf, °) are entered and preserved — conversions
 * happen internally. No fabricated peak power or F-v profile from height alone.
 */
const G = 9.80665;

const TESTS = [
  { key: "cmj",   label: "Countermovement Jump", match: "Countermovement", kind: "vjump" },
  { key: "sj",    label: "Squat Jump",           match: "Squat Jump (SJ)", kind: "vjump" },
  { key: "vj",    label: "Vertical Jump",        match: "Standing Vertical", kind: "vjump" },
  { key: "dj",    label: "Depth Jump",           match: "Depth Jump", kind: "depth" },
  { key: "broad", label: "Broad Jump",           match: "Standing Broad", kind: "broad" },
  { key: "1rm",   label: "1RM Strength Test",    match: "1RM", kind: "strength" },
  { key: "dyno",  label: "Isometric Dynamometer",match: "Isometric Peak Force", kind: "dyno" },
  { key: "rom",   label: "Joint ROM",            match: "Range of Motion", kind: "rom" },
  { key: "fv",    label: "Force–Velocity Assessment", match: null, kind: "fv" },
];

// Defined at module scope so inputs keep focus across re-renders.
const Fld = ({ label, children }) => (<div><div className="fl-label mb-1">{label}</div>{children}</div>);
const num = (v) => (v === "" || v == null ? null : parseFloat(v));

export default function RecordTestForm({ defs, assignments = [], patientId, onSaved, onCancel }) {
  const inp = "fl-input w-full text-sm px-2 py-2 rounded-md";
  const [testKey, setTestKey] = useState("cmj");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    external_load_kg: "0", body_mass_kg: "", jump_height_cm: "",
    drop_height_cm: "", rebound_height_cm: "", contact_time_ms: "",
    distance_cm: "", one_rm_kg: "", peak_force_kgf: "", time_to_peak_s: "",
    total_duration_s: "", rom_degrees: "", joint_angle: "",
    side: "bilateral", trial_no: "1", protocol: "",
    performed_at: new Date().toISOString().slice(0, 10), notes: "", released: false,
    exercise_def_id: "", assignment_id: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const test = TESTS.find((t) => t.key === testKey);

  // Map the chosen test to an approved definition (1RM lets the clinician pick the exercise).
  const oneRmDefs = useMemo(
    () => (defs || []).filter((d) => d.category === "dynamic_strength_1rm"),
    [defs]);
  const matchedDef = useMemo(() => {
    if (!test?.match) return null;
    if (test.key === "1rm") return (defs || []).find((d) => d.id === f.exercise_def_id) || null;
    return (defs || []).find((d) => (d.name || "").includes(test.match)) || null;
  }, [defs, test, f.exercise_def_id]);

  const sidedMode = matchedDef?.sided_mode || "either";
  // Default the side to a VALID option for the chosen definition so the first
  // save of a unilateral test (dynamometer / ROM) isn't rejected as bilateral.
  useEffect(() => {
    setF((s) => {
      if (sidedMode === "unilateral" && s.side === "bilateral") return { ...s, side: "left" };
      if (sidedMode === "bilateral" && s.side !== "bilateral") return { ...s, side: "bilateral" };
      return s;
    });
  }, [sidedMode]);

  // Live derived preview (display only — backend recomputes & stores).
  const takeoff = useMemo(() => {
    const h = num(f.jump_height_cm);
    return h != null && h >= 0 ? Math.sqrt(2 * G * (h / 100)).toFixed(2) : null;
  }, [f.jump_height_cm]);
  const rsi = useMemo(() => {
    const reb = num(f.rebound_height_cm), ct = num(f.contact_time_ms);
    return reb != null && reb >= 0 && ct != null && ct > 0 ? ((reb / 100) / (ct / 1000)).toFixed(2) : null;
  }, [f.rebound_height_cm, f.contact_time_ms]);
  const relStr = useMemo(() => {
    const r = num(f.one_rm_kg), bm = num(f.body_mass_kg);
    return r != null && bm && bm > 0 ? (r / bm).toFixed(2) : null;
  }, [f.one_rm_kg, f.body_mass_kg]);
  const forceN = useMemo(() => {
    const p = num(f.peak_force_kgf);
    return p != null && p >= 0 ? (p * G).toFixed(1) : null;
  }, [f.peak_force_kgf]);

  const save = async () => {
    if (test.kind === "fv") return;
    const def = matchedDef;
    if (!def) { toast.error(test.key === "1rm" ? "Choose the 1RM exercise." : "That test definition isn't available."); return; }
    let value, metrics = {};
    if (test.kind === "vjump") {
      value = num(f.jump_height_cm);
      if (value == null) { toast.error("Enter jump height (cm)."); return; }
      metrics = { jump_height_cm: value, external_load_kg: num(f.external_load_kg) ?? 0, body_mass_kg: num(f.body_mass_kg) };
    } else if (test.kind === "depth") {
      value = num(f.rebound_height_cm);
      if (value == null || num(f.contact_time_ms) == null) { toast.error("Enter rebound height (cm) and contact time (ms)."); return; }
      if (num(f.contact_time_ms) <= 0) { toast.error("Contact time must be greater than zero."); return; }
      metrics = { rebound_height_cm: value, contact_time_ms: num(f.contact_time_ms), drop_height_cm: num(f.drop_height_cm) };
    } else if (test.kind === "broad") {
      value = num(f.distance_cm);
      if (value == null) { toast.error("Enter horizontal distance (cm)."); return; }
      metrics = { distance_cm: value };
    } else if (test.kind === "strength") {
      value = num(f.one_rm_kg);
      if (value == null) { toast.error("Enter the measured 1RM (kg)."); return; }
      metrics = { one_rm_kg: value, body_mass_kg: num(f.body_mass_kg) };
    } else if (test.kind === "dyno") {
      value = num(f.peak_force_kgf);
      if (value == null) { toast.error("Enter peak force (kgf)."); return; }
      const ttp = num(f.time_to_peak_s), dur = num(f.total_duration_s);
      if (ttp != null && dur != null && (ttp < 0 || ttp > dur)) { toast.error("Time to peak must be between 0 and total duration."); return; }
      metrics = { peak_force_kgf: value, time_to_peak_s: ttp, total_duration_s: dur, joint_angle_deg: num(f.joint_angle) };
    } else if (test.kind === "rom") {
      value = num(f.rom_degrees);
      if (value == null) { toast.error("Enter ROM (degrees)."); return; }
      metrics = { rom_degrees: value };
    }
    setBusy(true);
    try {
      await api.post("/physical-tests/results", {
        test_definition_id: def.id, patient_id: patientId,
        battery_assignment_id: f.assignment_id || null,
        side: f.side, value: parseFloat(value),
        performed_at: new Date(f.performed_at).toISOString(),
        notes: f.notes || null, released: f.released, source: "clinician_manual",
        body_mass_kg: num(f.body_mass_kg), external_load_kg: num(f.external_load_kg),
        trial_no: num(f.trial_no), metrics,
      });
      toast.success("Measurement recorded.");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };


  return (
    <div className="fl-card p-4 mb-4 border border-[#f47a20]/30" data-testid="pt-record-form">
      <div className="fl-label mb-2">Select test</div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="pt-test-selector">
        {TESTS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTestKey(t.key)}
            data-testid={`pt-test-${t.key}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${testKey === t.key
              ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10" : "border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {test.kind === "fv" && (
        <FvAssessment patientId={patientId} onSaved={onSaved} />
      )}

      {test.kind !== "fv" && matchedDef && (
        <div className="flex items-center gap-2 mb-3 text-xs text-zinc-400" data-testid="pt-record-protocol">
          <ProtocolInfo def={matchedDef} slug={test.key} />
          <span>Testing protocol &amp; evidence for <span className="text-zinc-200">{matchedDef.name}</span></span>
        </div>
      )}

      {test.kind !== "fv" && (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            {test.key === "1rm" && (
              <Fld label="Exercise *">
                <select className={inp} value={f.exercise_def_id} onChange={(e) => set("exercise_def_id", e.target.value)} data-testid="pt-f-exercise">
                  <option value="">Select exercise…</option>
                  {oneRmDefs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Fld>
            )}
            {test.kind === "vjump" && (<>
              <Fld label="External load (kg)"><input type="number" step="any" className={inp} value={f.external_load_kg} onChange={(e) => set("external_load_kg", e.target.value)} data-testid="pt-f-load" /></Fld>
              <Fld label="Jump height (cm) *"><input type="number" step="any" className={inp} value={f.jump_height_cm} onChange={(e) => set("jump_height_cm", e.target.value)} data-testid="pt-f-jump-height" /></Fld>
              <Fld label="Body mass (kg)"><input type="number" step="any" className={inp} value={f.body_mass_kg} onChange={(e) => set("body_mass_kg", e.target.value)} data-testid="pt-f-bodymass" /></Fld>
            </>)}
            {test.kind === "depth" && (<>
              <Fld label="Drop / box height (cm)"><input type="number" step="any" className={inp} value={f.drop_height_cm} onChange={(e) => set("drop_height_cm", e.target.value)} data-testid="pt-f-drop" /></Fld>
              <Fld label="Rebound jump height (cm) *"><input type="number" step="any" className={inp} value={f.rebound_height_cm} onChange={(e) => set("rebound_height_cm", e.target.value)} data-testid="pt-f-rebound" /></Fld>
              <Fld label="Ground contact time (ms) *"><input type="number" step="any" className={inp} value={f.contact_time_ms} onChange={(e) => set("contact_time_ms", e.target.value)} data-testid="pt-f-contact" /></Fld>
            </>)}
            {test.kind === "broad" && (
              <Fld label="Horizontal distance (cm) *"><input type="number" step="any" className={inp} value={f.distance_cm} onChange={(e) => set("distance_cm", e.target.value)} data-testid="pt-f-distance" /></Fld>
            )}
            {test.kind === "strength" && (<>
              <Fld label="Measured 1RM (kg) *"><input type="number" step="any" className={inp} value={f.one_rm_kg} onChange={(e) => set("one_rm_kg", e.target.value)} data-testid="pt-f-1rm" /></Fld>
              <Fld label="Body mass (kg)"><input type="number" step="any" className={inp} value={f.body_mass_kg} onChange={(e) => set("body_mass_kg", e.target.value)} data-testid="pt-f-bodymass" /></Fld>
            </>)}
            {test.kind === "dyno" && (<>
              <Fld label="Peak force (kgf) *"><input type="number" step="any" className={inp} value={f.peak_force_kgf} onChange={(e) => set("peak_force_kgf", e.target.value)} data-testid="pt-f-peak" /></Fld>
              <Fld label="Time to peak (s)"><input type="number" step="any" className={inp} value={f.time_to_peak_s} onChange={(e) => set("time_to_peak_s", e.target.value)} data-testid="pt-f-ttp" /></Fld>
              <Fld label="Total duration (s)"><input type="number" step="any" className={inp} value={f.total_duration_s} onChange={(e) => set("total_duration_s", e.target.value)} data-testid="pt-f-dur" /></Fld>
              <Fld label="Joint angle (°)"><input type="number" step="any" className={inp} value={f.joint_angle} onChange={(e) => set("joint_angle", e.target.value)} data-testid="pt-f-angle" /></Fld>
            </>)}
            {test.kind === "rom" && (
              <Fld label="ROM (degrees) *"><input type="number" step="any" className={inp} value={f.rom_degrees} onChange={(e) => set("rom_degrees", e.target.value)} data-testid="pt-f-rom" /></Fld>
            )}

            {(test.kind === "broad" || test.kind === "dyno" || test.kind === "rom") && (
              <Fld label={sidedMode === "unilateral" ? "Side *" : "Side"}>
                <select className={inp} value={f.side} onChange={(e) => set("side", e.target.value)} data-testid="pt-f-side">
                  {(sidedMode === "unilateral"
                    ? [["left", "Left"], ["right", "Right"]]
                    : sidedMode === "bilateral"
                      ? [["bilateral", "Bilateral"]]
                      : [["bilateral", "Bilateral"], ["left", "Left"], ["right", "Right"]]
                  ).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Fld>
            )}
            <Fld label="Trial #"><input type="number" step="1" className={inp} value={f.trial_no} onChange={(e) => set("trial_no", e.target.value)} data-testid="pt-f-trial" /></Fld>
            <Fld label="Date"><input type="date" className={inp} value={f.performed_at} onChange={(e) => set("performed_at", e.target.value)} data-testid="pt-f-date" /></Fld>
            <Fld label="Notes"><input className={inp} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pt-f-notes" /></Fld>
          </div>

          {/* Live derived preview */}
          {takeoff && test.kind === "vjump" && (
            <div className="mt-3" data-testid="pt-calc-takeoff">
              <div className="fl-label">Estimated take-off velocity</div>
              <div className="fl-heading text-2xl text-[#f47a20]">{takeoff} m/s</div>
              <div className="text-[11px] text-zinc-500">Calculated from flight-phase jump height under ballistic assumptions. Not mean push-off velocity.</div>
            </div>
          )}
          {rsi && test.kind === "depth" && (
            <div className="mt-3" data-testid="pt-calc-rsi">
              <div className="fl-label">Reactive Strength Index (RSI)</div>
              <div className="fl-heading text-2xl text-[#f47a20]">{rsi} m/s</div>
              <div className="text-[11px] text-zinc-500">RSI = jump height (m) ÷ contact time (s). Height/contact-time definition.</div>
            </div>
          )}
          {relStr && test.kind === "strength" && (
            <div className="mt-3" data-testid="pt-calc-relstr"><div className="fl-label">Relative strength</div><div className="fl-heading text-2xl text-[#f47a20]">{relStr} ×BM</div></div>
          )}
          {forceN && test.kind === "dyno" && (
            <div className="mt-3" data-testid="pt-calc-forcen"><div className="fl-label">Peak force</div><div className="fl-heading text-2xl text-[#f47a20]">{forceN} N</div><div className="text-[11px] text-zinc-500">kgf × 9.80665. Confirm the device unit is kilogram-force.</div></div>
          )}

          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Fld label="Linked battery (optional)">
              <select className={inp} value={f.assignment_id} onChange={(e) => set("assignment_id", e.target.value)} data-testid="pt-f-assignment">
                <option value="">None — quick record</option>
                {assignments.map((a) => <option key={a.id} value={a.id}>{a.battery_name} · {a.context}</option>)}
              </select>
            </Fld>
            <label className="flex items-center gap-2 text-sm text-zinc-300 self-end pb-2 cursor-pointer" data-testid="pt-f-released">
              <input type="checkbox" checked={f.released} onChange={(e) => set("released", e.target.checked)} />
              Release this result to the patient immediately
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={save} disabled={busy} className="fl-btn text-sm" data-testid="pt-record-save">{busy ? "Saving…" : "Save result"}</button>
            <button onClick={onCancel} className="fl-btn-ghost text-sm">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
